<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import DegreeDetailsModal from './DegreeDetailsModal.vue'
import { IExtendedDegree } from '@shared/types/extensions'
import { ref as dbRef, get, query, orderByChild, equalTo } from 'firebase/database'
import { database } from '@/firebase/config'
import { IDegreePackage } from '@shared/types'
import { formatDate } from '@/utils/dateFormatter'
const props = defineProps<{
	modelValue: boolean
	packageData: IDegreePackage
	institutionId: string
	testMode: boolean
}>()

const emit = defineEmits<(e: 'update:modelValue', value: boolean) => void>()
const showModal = computed({
	get: () => props.modelValue,
	set: value => emit('update:modelValue', value)
})
const degrees = ref<IExtendedDegree[]>([])
const loading = ref(false)
const db = database
const showDegreeDetailsModal = ref(false)
const selectedDegree = ref<IExtendedDegree | null>(null)

const getInstitutionPath = (path: string) => `institutions/${props.institutionId}${props.testMode ? '/testMode' : ''}${path}`

const loadPackageDegrees = async () => {
	if (!props.packageData || !props.packageData.id) {
		return
	}

	try {
		loading.value = true
		const degreesRef = dbRef(db, getInstitutionPath('/degrees/queue'))
		const packageQuery = query(degreesRef, orderByChild('package'), equalTo(Number(props.packageData.id)))
		const snapshot = await get(packageQuery)

		if (!snapshot.exists()) {
			degrees.value = []
			return
		}

		const documents = snapshot.val()
		const loadedDegrees: IExtendedDegree[] = []

		Object.entries(documents).forEach(([id, data]: [string, any]) => {
			const degree: IExtendedDegree = {
				id,
				studentName: `${data.data?.Profesionista?.nombre || ''} ${data.data?.Profesionista?.primerApellido || ''} ${data.data?.Profesionista?.segundoApellido || ''}`.trim(),
				status: data.status || 'pending',
				selected: false,
				data: data.data,
				folio: data.folio || '',
				date: data.date || '',
				folioControl: data.folioControl || '',
				xml: data.xml || '',
				cadenaOriginal: data.cadenaOriginal || '',
				controlNumber: data.controlNumber || '',
				warning: data.warning || '',
				error: data.error || '',
				career: data.data?.Carrera?.nombreCarrera,
				package: data.package,
				xmlAuthenticated: data.xmlAuthenticated || '',
				type: data.type || '',
				success: data.success || false
			}
			loadedDegrees.push(degree)
		})

		degrees.value = loadedDegrees.sort((a, b) => (new Date(b.date)?.getTime() || 0) - (new Date(a.date)?.getTime() || 0))
	} catch (error) {
		console.error('Error loading package degrees:', error)
		degrees.value = []
	} finally {
		loading.value = false
	}
}

// Función para ver detalles de un título
const viewDegreeDetails = (degree: IExtendedDegree) => {
	selectedDegree.value = degree
	showDegreeDetailsModal.value = true
}

// Cargar los títulos cuando se abre el modal o cambia el paquete
watch(
	() => props.modelValue,
	newValue => {
		if (newValue) {
			loadPackageDegrees()
		}
	}
)

// También observar cambios en packageData
watch(
	() => props.packageData,
	newValue => {
		console.log('packageData cambió:', newValue)
		if (props.modelValue) {
			loadPackageDegrees()
		}
	},
	{ deep: true }
)

// Cargar datos al montar el componente si el modal está abierto
onMounted(() => {
	if (props.modelValue) {
		loadPackageDegrees()
	}
})

const closeModalDetails = () => {
	showModal.value = false
}
</script>
<template>
	<VaModal v-model="showModal" hide-default-actions noDismiss noEscDismiss noOutsideDismiss background-color="transparent">
		<VaCard v-if="packageData" class="max-w-4xl mx-auto">
			<!-- Header mejorado -->
			<VaCardTitle :class="[props.testMode ? 'bg-gradient-paquete-test' : 'bg-gradient-paquete', 'rounded-t-xl']">
				<div class="row align-items-center">
					<div class="col-md-11 text-left">
						<VaIcon name="description" class="text-white" />
						<h2 class="text-xl font-bold text-white">Detalles del Paquete</h2>
						<p class="text-sm text-blue-100 mt-1 text-white">Información completa del paquete de títulos.</p>
					</div>
					<div class="col-md-1 text-right ml2">
						<VaButton round icon="cancel" :color="!props.testMode ? '#0d6efd' : '#7f1d1d'" type="button" @click="closeModalDetails" style="color: white; font-weight: bold" />
					</div>
				</div>
			</VaCardTitle>

			<VaCardContent class="p-6">
				<!-- Información del Paquete -->
				<section class="package-section">
					<h3 class="section-title">
						<VaIcon name="description" class="title-icon" />
						Información del Paquete
					</h3>
					<div class="package-grid">
						<!-- Nombre del Paquete -->
						<div class="package-card">
							<div class="package-header">
								<div class="package-avatar">
									<VaIcon name="badge" class="package-icon" />
								</div>
								<div class="package-info">
									<h5 class="package-name">Nombre del Paquete</h5>
									<p class="package-value">{{ packageData.label }}</p>
								</div>
							</div>
						</div>

						<!-- Fecha -->
						<div class="package-card">
							<div class="package-header">
								<div class="package-avatar">
									<VaIcon name="event" class="package-icon" />
								</div>
								<div class="package-info">
									<h5 class="package-name">Fecha</h5>
									<p class="package-value">{{ formatDate(packageData.id) }}</p>
								</div>
							</div>
						</div>

						<!-- Número de Títulos -->
						<div class="package-card">
							<div class="package-header">
								<div class="package-avatar">
									<VaIcon name="collections_bookmark" class="package-icon" />
								</div>
								<div class="package-info">
									<h5 class="package-name">Títulos</h5>
									<p class="package-value">{{ packageData.numDegrees }}</p>
								</div>
							</div>
						</div>

						<!-- Estado de Firma -->
						<div class="package-card">
							<div class="package-header">
								<div class="package-avatar">
									<VaIcon name="check_circle" class="package-icon" />
								</div>
								<div class="package-info">
									<h5 class="package-name">Estado</h5>
									<p :class="['package-value', packageData.sign ? 'text-success' : 'text-warning']">
										{{ packageData.sign ? 'Firmado' : 'Pendiente' }}
									</p>
								</div>
							</div>
						</div>
					</div>
				</section>

				<!-- Responsables de Firma -->
				<section class="signers-section">
					<h4 class="signers-title">
						<VaIcon name="groups" class="title-icon" />
						Responsables de Firma
					</h4>
					<div class="signers-grid">
						<div v-for="(responsable, key) in packageData.responsables" :key="key" class="signer-card">
							<div class="signer-header">
								<div class="signer-avatar">
									<VaIcon name="person" class="signer-icon" />
								</div>
								<div class="signer-info">
									<h5 class="signer-name">{{ responsable.name }}</h5>
									<p class="signer-role">{{ responsable.cargo }}</p>
								</div>
								<div class="signer-status">
									<span :class="['status-badge', responsable.sign ? 'status-signed' : 'status-pending']">
										{{ responsable.sign ? 'Firmado' : 'Pendiente' }}
									</span>
									<VaIcon v-if="responsable.sign" name="check_circle" class="sign-icon" />
								</div>
							</div>
						</div>
					</div>
				</section>

				<!-- Estudiantes del Paquete -->
				<section class="students-section">
					<h4 class="section-title">
						<VaIcon name="school" class="title-icon" />
						Estudiantes del Paquete
					</h4>

					<div v-if="loading" class="loading-container">
						<div class="loader mb-4"></div>
						<p class="text-lg text-gray-700 font-medium">Cargando títulos...</p>
					</div>

					<div v-else-if="degrees.length === 0" class="empty-state">
						<div class="empty-icon">
							<VaIcon name="info" class="w-12 h-12 text-gray-400" />
						</div>
						<p class="text-gray-500 text-lg">No se encontraron títulos en este paquete.</p>
					</div>

					<div v-else class="students-grid">
						<div v-for="degree in degrees" :key="degree.id" class="student-card">
							<div class="student-header">
								<div class="student-avatar">
									<VaIcon name="person" class="student-icon" />
								</div>
								<div class="student-info">
									<h5 class="student-name">
										{{ degree.data?.Profesionista?.nombre }}
										{{ degree.data?.Profesionista?.primerApellido }}
										{{ degree.data?.Profesionista?.segundoApellido }}
									</h5>
									<p class="student-career">{{ degree.data?.Carrera?.nombreCarrera || 'Carrera no especificada' }}</p>
								</div>
							</div>

							<div class="student-details">
								<div class="detail-item">
									<VaIcon name="fingerprint" class="detail-icon" />
									<span class="detail-label">Folio:</span>
									<span class="detail-value">{{ degree.folioControl || 'N/A' }}</span>
								</div>
								<div class="detail-item">
									<VaIcon name="badge" class="detail-icon" />
									<span class="detail-label">CURP:</span>
									<span class="detail-value">{{ degree.data?.Profesionista?.curp || 'N/A' }}</span>
								</div>
							</div>

							<div class="student-actions">
								<button class="btn btn-primary btn-sm" @click="viewDegreeDetails(degree)">
									<VaIcon name="visibility" class="btn-icon" />
									Ver Detalles
								</button>
							</div>
						</div>
					</div>
				</section>
			</VaCardContent>
		</VaCard>
	</VaModal>

	<!-- Modal de detalles del título -->
	<DegreeDetailsModal v-if="showDegreeDetailsModal" v-model="showDegreeDetailsModal" :degree="selectedDegree!" :test-mode="testMode" :institution-id="institutionId" :package-id="packageData.id" />
</template>

<style scoped>
/* Loader mejorado */
.loader {
	border: 4px solid #f3f4f6;
	border-radius: 50%;
	border-top: 4px solid #007bff;
	width: 60px;
	height: 60px;
	animation: spin 1s linear infinite;
	box-shadow: 0 4px 12px rgba(0, 123, 255, 0.2);
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

/* Estados de texto */

.text-success {
	color: #28a745;
}
.text-warning {
	color: #ffc107;
}

/* Secciones principales */
.package-section,
.signers-section,
.students-section {
	margin-top: 1rem;
	margin-bottom: 1rem;
}

.section-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1a2a3a;
	margin-bottom: 1.5rem;
	padding-bottom: 0.75rem;
	border-bottom: 2px solid #e9ecef;
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.title-icon {
	color: #007bff;
	font-size: 1.25rem;
}

/* Grid de paquetes */
.package-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
	gap: 1rem;
}

.package-card {
	background: white;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	padding: 1.25rem;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.package-card:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

.package-header {
	display: flex;
	align-items: center;
	gap: 1rem;
}

.package-avatar {
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background: linear-gradient(135deg, #6ab0ff 0%, #0873c4 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 1.25rem;
	flex-shrink: 0;
}

.package-icon {
	color: white;
	font-size: 1.25rem;
}

.package-info {
	flex: 1;
	min-width: 0;
}

.package-name {
	font-size: 0.875rem;
	font-weight: 500;
	color: #6c757d;
	margin: 0 0 0.25rem 0;
	text-transform: uppercase;
	letter-spacing: 0.025em;
}

.package-value {
	font-size: 1rem;
	font-weight: 600;
	color: #1a2a3a;
	margin: 0;
	line-height: 1.3;
}

/* Sección de firmantes */
.signers-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1a2a3a;
	margin-bottom: 1.5rem;
	padding-bottom: 0.75rem;
	border-bottom: 2px solid #e9ecef;
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.signers-grid {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.signer-card {
	background: white;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	padding: 1.25rem;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.signer-card:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

.signer-header {
	display: flex;
	align-items: center;
	gap: 1rem;
}

.signer-avatar {
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background: linear-gradient(135deg, #6ab0ff 0%, #0873c4 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 1.25rem;
	flex-shrink: 0;
}

.signer-icon {
	color: white;
	font-size: 1.25rem;
}

.signer-info {
	flex: 1;
	min-width: 0;
}

.signer-name {
	font-size: 1.1rem;
	font-weight: 600;
	color: #1a2a3a;
	margin: 0 0 0.25rem 0;
	line-height: 1.3;
}

.signer-role {
	font-size: 0.9rem;
	color: #6c757d;
	margin: 0;
	font-weight: 500;
}

.signer-status {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-shrink: 0;
}

.status-badge {
	padding: 0.25rem 0.75rem;
	border-radius: 12px;
	font-size: 0.75rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.025em;
}

.status-signed {
	background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
	color: #166534;
	border: 1px solid #86efac;
}

.status-pending {
	background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
	color: #92400e;
	border: 1px solid #fcd34d;
}

.sign-icon {
	color: #28a745;
	font-size: 1.25rem;
	animation: fadeIn 0.5s ease-in-out;
}

/* Sección de estudiantes */
.students-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: 1rem;
	max-height: 500px;
	overflow-y: auto;
	padding-right: 0.5rem;
}

.student-card {
	background: white;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	padding: 1.25rem;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.student-card:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

.student-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1rem;
}

.student-avatar {
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background: linear-gradient(135deg, #6ab0ff 0%, #0873c4 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 1.25rem;
	flex-shrink: 0;
}

.student-icon {
	color: white;
	font-size: 1.25rem;
}

.student-info {
	flex: 1;
	min-width: 0;
}

.student-name {
	font-size: 1.1rem;
	font-weight: 600;
	color: #1a2a3a;
	margin: 0 0 0.25rem 0;
	line-height: 1.3;
}

.student-career {
	font-size: 0.9rem;
	color: #6c757d;
	margin: 0;
	font-weight: 500;
}

.student-details {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	margin-bottom: 1rem;
}

.detail-item {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.5rem;
	background: #f8f9fa;
	border-radius: 8px;
	border-left: 3px solid #007bff;
}

.detail-icon {
	color: #007bff;
	font-size: 1rem;
	flex-shrink: 0;
}

.detail-label {
	font-size: 0.875rem;
	font-weight: 500;
	color: #6c757d;
	min-width: 60px;
}

.detail-value {
	font-size: 0.875rem;
	font-weight: 600;
	color: #1a2a3a;
	flex: 1;
}

.student-actions {
	display: flex;
	justify-content: flex-end;
}

/* Estados de carga y vacío */
.loading-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 3rem 1rem;
}

.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 3rem 1rem;
	text-align: center;
}

.empty-icon {
	margin-bottom: 1rem;
}

/* Botones */
.btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0.5rem 1rem;
	border-radius: 8px;
	font-weight: 600;
	font-size: 0.875rem;
	transition: all 0.3s ease;
	border: none;
	cursor: pointer;
	text-decoration: none;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn:hover {
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
	transform: none;
}

.btn-primary {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	color: white;
}

.btn-primary:hover {
	background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
}

.btn-sm {
	padding: 0.375rem 0.75rem;
	font-size: 0.75rem;
}

.btn-icon {
	width: 1rem;
	height: 1rem;
	margin-right: 0.5rem;
}

/* Scroll personalizado */
.students-grid::-webkit-scrollbar {
	width: 8px;
}

.students-grid::-webkit-scrollbar-track {
	background: #f1f5f9;
	border-radius: 4px;
}

.students-grid::-webkit-scrollbar-thumb {
	background: linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%);
	border-radius: 4px;
}

.students-grid::-webkit-scrollbar-thumb:hover {
	background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
}

/* Animaciones */
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

.package-card,
.signer-card,
.student-card {
	animation: fadeIn 0.5s ease-in-out;
}

/* Responsive design */
@media (max-width: 768px) {
	.package-grid,
	.students-grid {
		grid-template-columns: 1fr;
	}

	.signer-header,
	.student-header {
		flex-direction: column;
		align-items: flex-start;
		gap: 0.75rem;
	}

	.signer-status {
		align-self: flex-end;
	}

	.signer-card,
	.package-card,
	.student-card {
		padding: 1rem;
	}

	.student-details {
		gap: 0.25rem;
	}

	.detail-item {
		flex-direction: column;
		align-items: flex-start;
		gap: 0.25rem;
	}

	.detail-label {
		min-width: auto;
	}
}
.bg-gradient-paquete {
	background: linear-gradient(90deg, #2563eb 0%, #1e3a8a 100%);
}
.bg-gradient-paquete-test {
	background: linear-gradient(90deg, #dc2626 0%, #7f1d1d 100%);
}
</style>
